import React, { FC, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Page } from '../../../components';
import {
  Box, Drawer, List, ListItem, ListItemText,
  IconButton, useMediaQuery, useTheme,
  Typography,
} from '@mui/material';
import { Home as HomeIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Routes } from '../../../constants';

import { SmpLoadingProvider, useSmpLoading } from '../context';
import { PageLoader } from '../components';

const LayoutWithLoader: FC = ({ children }) => {
  const { isLoading } = useSmpLoading();
  return (
    <>
      <PageLoader active={isLoading} />
      {children}
    </>
  );
};

const drawerWidth = 200;

const sidebarOptions = [
  {
    key: 'About',
    getLabel: (hasAppId: boolean) =>
      hasAppId ? 'About Application' : 'Applications',
    showWhenNoAppId: true,
    showWhenAppId: true,
  },
  {
    key: 'Licenses',
    getLabel: () => 'Licenses',
    showWhenNoAppId: true,
    showWhenAppId: true,
  },
  {
    key: 'Users',
    getLabel: () => 'Users',
    showWhenNoAppId: false,
    showWhenAppId: true,
  },
  {
    key: 'Employees',
    getLabel: () => 'Employees',
    showWhenNoAppId: true,
    showWhenAppId: false,
  },
  {
    key: 'Chargebacks',
    getLabel: () => 'Chargebacks',
    showWhenNoAppId: false,
    showWhenAppId: true,
  },
];

const SmpLayout: FC = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation();

  // Extract applicationId if present
  let applicationId: string | undefined;
  const m1 = location.pathname.match(/\/smp\/application\/view\/(\d+)/);
  const m2 = location.pathname.match(
    /\/smp\/application\/(\d+)\/(?:users|licenses|chargebacks)/
  );
  if (m1) applicationId = m1[1];
  else if (m2) applicationId = m2[1];

  const getRoute = (key: string) => {
    if (!applicationId) {
      if (key === 'About') return `${Routes.smp}/dashboard`;
      if (key === 'Employees') return `/smp/employees`;
      return `/smp/${key.toLowerCase()}`;
    }
    switch (key) {
      case 'About':
        return Routes.viewApplication(applicationId);
      case 'Users':
        return Routes.applicationUsers(applicationId);
      case 'Licenses':
        return Routes.applicationLicenses(applicationId);
      case 'Chargebacks':
        return Routes.applicationChargebacks(applicationId);
      case 'Employees':
        return `/smp/employees`;
      default:
        return `/smp/${key.toLowerCase()}`;
    }
  };

  const visibleOptions = sidebarOptions.filter(
    (opt) =>
      (applicationId && opt.showWhenAppId) ||
      (!applicationId && opt.showWhenNoAppId)
  );

  // New: detect "raw" /smp/licenses route
  const isLicensesRoot =
    !applicationId && location.pathname.endsWith('/licenses');

  return (
    <Page title="SMP">
      <SmpLoadingProvider>
        <LayoutWithLoader>
          <Box sx={{ display: 'flex', width: '100%' }}>
            {(sidebarOpen || isMobile) && (
                <Drawer
                  variant={isMobile ? 'temporary' : 'persistent'}
                  anchor="left"
                  open={sidebarOpen}
                  onClose={() => setSidebarOpen(false)}
                  ModalProps={{ keepMounted: true }}
                  sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                      width: drawerWidth,
                      boxSizing: 'border-box',
                      height: '100vh',
                      position: 'sticky',
                      top: '64px', 
                      zIndex: 1,
                      overflowY: 'auto',
                    },
                  }}
                >

                {/* Home + close */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 1,
                    py: 1,
                  }}
                >
                  <IconButton
                    component={NavLink}
                    to="/smp/dashboard"
                    size="small"
                    title="Home"
                    edge="start"
                  >
                    <HomeIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setSidebarOpen(false)}
                    size="small"
                    title="Close sidebar"
                  >
                    <ChevronLeft />
                  </IconButton>
                </Box>

                <List>
                  {visibleOptions.map(({ key, getLabel }) => (
                    <ListItem
                      button
                      key={key}
                      component={NavLink}
                      to={getRoute(key)}
                      activeClassName="Mui-selected"
                      onClick={() => isMobile && setSidebarOpen(false)}
                    >
                      <ListItemText primary={getLabel(!!applicationId)} />
                    </ListItem>
                  ))}
                </List>
              </Drawer>
            )}

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                width: `calc(100% - ${
                  sidebarOpen && !isMobile ? drawerWidth : 0
                }px)`,
                px: { xs: 1, sm: 2 },
                pt: 2,
                transition: 'all 0.3s ease',
              }}
            >
              {/* collapsed toggle + home */}
              {!sidebarOpen && !isMobile && (
                <Box
                  sx={{
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    component={NavLink}
                    to="/smp/dashboard"
                    size="small"
                    title="Home"
                    sx={{ mr: 1 }}
                  >
                    <HomeIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setSidebarOpen(true)}
                    size="small"
                    title="Open sidebar"
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              )}

              {/* if raw /smp/licenses*/}
              {isLicensesRoot ? (
                <Box p={4} textAlign="center">
                  <Typography variant="h5" gutterBottom>
               
                  </Typography>
                
                </Box>
              ) : (
                children
              )}
            </Box>
          </Box>
        </LayoutWithLoader>
      </SmpLoadingProvider>
    </Page>
  );
};

export default SmpLayout;
